'use client';

import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { CheckCircle, PauseCircle, PlayCircle, Clock } from 'lucide-react';

interface AuctionStatusProps {
  status: 'waiting' | 'active' | 'paused' | 'completed';
  tournamentName: string;
}

const STATUS_CONFIG = {
  waiting: {
    text: 'Waiting to Start',
    color: 'bg-accent-blue text-white',
    icon: Clock,
    animation: {
      scale: [1, 1.1, 1],
      transition: { duration: 1.5, repeat: Infinity },
    },
  },
  active: {
    text: 'Auction Live',
    color: 'bg-accent-green text-white ring-2 ring-green-400 shadow-lg',
    icon: PlayCircle,
    animation: {
      scale: [1, 1.2, 1],
      transition: { duration: 1, repeat: Infinity },
    },
  },
  paused: {
    text: 'Auction Paused',
    color: 'bg-accent-yellow text-gray-900',
    icon: PauseCircle,
    animation: { scale: 1 },
  },
  completed: {
    text: 'Auction Completed',
    color: 'bg-green-400 text-white',
    icon: CheckCircle,
    animation: { scale: 1 },
  },
};

export function AuctionStatus({ status }: AuctionStatusProps) {
  const { text, color, icon: Icon, animation } = STATUS_CONFIG[status];

  return (
    <motion.div
      className="flex items-center justify-center space-x-2"
      animate={animation}
    >
      <div
        className={cn(
          'flex h-8 w-8 items-center justify-center rounded-full',
          color
        )}
      >
        <Icon className="h-4 w-4" />
      </div>
      <span className="text-sm font-semibold text-gray-900">{text}</span>
    </motion.div>
  );
}
